/**
 * Internationalization (i18n) utilities
 */

export type Language = "en" | "id";

export const SUPPORTED_LANGUAGES: Array<{ code: Language; name: string; nativeName: string }> = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia" },
];

// Translation keys
export type TranslationKey = 
  // Dashboard
  | "dashboard.title"
  | "dashboard.subtitle"
  | "dashboard.financialHealth"
  | "dashboard.netWorth"
  | "dashboard.savingsRate"
  | "dashboard.debtToAssetRatio"
  | "dashboard.monthlyIncome"
  | "dashboard.monthlyExpenses"
  | "dashboard.savings"
  | "dashboard.thisMonth"
  | "dashboard.assets"
  | "dashboard.debts"
  | "dashboard.recentTransactions"
  | "dashboard.viewAll"
  | "dashboard.noTransactions"
  | "dashboard.addTransaction"
  | "dashboard.incomeVsExpenses"
  | "dashboard.topCategories"
  | "dashboard.viewReports"
  | "dashboard.totalFinancialPosition"
  | "dashboard.excellentSavingsRate"
  | "dashboard.goodSavingsRate"
  | "dashboard.needsImprovement"
  | "dashboard.healthyRatio"
  | "dashboard.moderateDebt"
  | "dashboard.highDebtLevel"
  | "dashboard.excellentFinancialShape"
  | "dashboard.goodFinancialHealth"
  | "dashboard.fairFinancialHealth"
  | "dashboard.poorFinancialHealth"
  | "dashboard.assetsVsDebts"
  | "dashboard.financialBalance"
  | "dashboard.netPosition"
  | "dashboard.latestIncomeAndExpenses"
  | "dashboard.ofIncome"
  | "dashboard.overspending"
  // Settings
  | "settings.title"
  | "settings.subtitle"
  | "settings.profile"
  | "settings.profileDescription"
  | "settings.currency"
  | "settings.currencyDescription"
  | "settings.language"
  | "settings.languageDescription"
  | "settings.password"
  | "settings.passwordDescription"
  | "settings.username"
  | "settings.email"
  | "settings.newPassword"
  | "settings.confirmPassword"
  | "settings.save"
  | "settings.saving"
  | "settings.updatePassword"
  | "settings.updating"
  | "settings.noChanges"
  | "settings.success"
  | "settings.error"
  // Budget
  | "budget.title"
  | "budget.subtitle"
  | "budget.totalIncome"
  | "budget.totalExpenses"
  | "budget.savings"
  | "budget.income"
  | "budget.expenses"
  | "budget.addIncome"
  | "budget.addExpense"
  | "budget.sourceName"
  | "budget.amount"
  | "budget.date"
  | "budget.category"
  | "budget.noIncomeRecords"
  | "budget.noExpenseRecords"
  | "budget.incomeAdded"
  | "budget.incomeUpdated"
  | "budget.expenseAdded"
  | "budget.expenseUpdated"
  | "budget.incomeDeleted"
  | "budget.expenseDeleted"
  | "budget.deleteIncomeConfirm"
  | "budget.deleteExpenseConfirm"
  | "budget.update"
  | "budget.showing"
  | "budget.to"
  | "budget.of"
  | "budget.entries"
  | "budget.previous"
  | "budget.next"
  // Net Worth
  | "netWorth.title"
  | "netWorth.subtitle"
  | "netWorth.totalAssets"
  | "netWorth.totalDebts"
  | "netWorth.assets"
  | "netWorth.debts"
  | "netWorth.addAsset"
  | "netWorth.addDebt"
  | "netWorth.interestRate"
  | "netWorth.dueDate"
  | "netWorth.noAssets"
  | "netWorth.noDebts"
  | "netWorth.recordPayment"
  | "netWorth.paymentAmount"
  | "netWorth.paymentRecorded"
  | "netWorth.assetDeleted"
  | "netWorth.debtDeleted"
  | "netWorth.deleteAssetConfirm"
  | "netWorth.deleteDebtConfirm"
  | "netWorth.optional"
  // Goals
  | "goals.title"
  | "goals.subtitle"
  | "goals.totalGoals"
  | "goals.completed"
  | "goals.inProgress"
  | "goals.budgetGoals"
  | "goals.debtReduction"
  | "goals.netWorthGrowth"
  | "goals.addGoal"
  | "goals.goalName"
  | "goals.targetAmount"
  | "goals.targetDate"
  | "goals.noGoals"
  | "goals.goalAdded"
  | "goals.goalUpdated"
  | "goals.goalDeleted"
  | "goals.deleteGoalConfirm"
  | "goals.progress"
  | "goals.status"
  | "goals.notStarted"
  | "goals.completedStatus"
  | "goals.daysRemaining"
  | "goals.totalDebt"
  | "goals.paid"
  | "goals.remaining"
  | "goals.overallProgress"
  | "goals.debtBreakdown"
  | "goals.original"
  | "goals.current"
  | "goals.setTarget"
  | "goals.updateTarget"
  | "goals.targetNetWorth"
  | "goals.currentNetWorth"
  | "goals.noNetWorthTarget"
  | "goals.clickSetTarget"
  // Reports
  | "reports.title"
  | "reports.subtitle"
  | "reports.monthlyReport"
  | "reports.netWorthReport"
  | "reports.incomeVsExpenses"
  | "reports.categoryBreakdown"
  | "reports.generate"
  | "reports.exportPDF"
  | "reports.exportCSV"
  | "reports.loadingReport"
  | "reports.incomeBySource"
  | "reports.expensesByCategory"
  | "reports.totalIncome"
  | "reports.totalExpenses"
  | "reports.savings"
  | "reports.savingsRate"
  // Notifications
  | "notifications.title"
  | "notifications.subtitle"
  | "notifications.totalNotifications"
  | "notifications.unread"
  | "notifications.all"
  | "notifications.markAllRead"
  | "notifications.markedAsRead"
  | "notifications.deleted"
  | "notifications.noNotifications"
  | "notifications.noUnread"
  | "notifications.viewNotifications"
  // Auth
  | "auth.login"
  | "auth.logout"
  | "auth.register"
  | "auth.loggingIn"
  | "auth.loginSuccess"
  | "auth.loginFailed"
  | "auth.createAccount"
  | "auth.creatingAccount"
  | "auth.accountCreated"
  | "auth.registrationFailed"
  | "auth.password"
  | "auth.confirmPassword"
  | "auth.passwordsNotMatch"
  | "auth.passwordTooShort"
  | "auth.dontHaveAccount"
  | "auth.alreadyHaveAccount"
  | "auth.welcome"
  | "auth.loginToAccess"
  | "auth.registerToStart"
  | "auth.dataSecure"
  | "auth.freeAccount"
  | "auth.freeAccountDescription"
  | "auth.dataSecureDescription"
  // Navbar
  | "navbar.dashboard"
  | "navbar.budget"
  | "navbar.netWorth"
  | "navbar.goals"
  | "navbar.reports"
  | "navbar.notifications"
  | "navbar.settings"
  | "navbar.more"
  | "navbar.logout"
  // Common
  | "common.skipToContent"
  | "common.loading"
  | "common.error"
  | "common.success"
  | "common.cancel"
  | "common.save"
  | "common.delete"
  | "common.edit"
  | "common.add"
  | "common.view"
  | "common.close"
  | "common.update"
  | "common.confirmDelete"
  | "common.justNow"
  | "common.minutesAgo"
  | "common.hoursAgo"
  | "common.daysAgo";

const translations: Record<Language, Record<TranslationKey, string>> = {
  en: {
    "dashboard.title": "Dashboard",
    "dashboard.subtitle": "Overview of your financial health",
    "dashboard.financialHealth": "Financial Health",
    "dashboard.netWorth": "Net Worth",
    "dashboard.savingsRate": "Savings Rate",
    "dashboard.debtToAssetRatio": "Debt-to-Asset Ratio",
    "dashboard.monthlyIncome": "Monthly Income",
    "dashboard.monthlyExpenses": "Monthly Expenses",
    "dashboard.savings": "Savings",
    "dashboard.thisMonth": "This month",
    "dashboard.assets": "Assets",
    "dashboard.debts": "Debts",
    "dashboard.recentTransactions": "Recent Transactions",
    "dashboard.viewAll": "View All",
    "dashboard.noTransactions": "No recent transactions",
    "dashboard.addTransaction": "Add Transaction",
    "dashboard.incomeVsExpenses": "Income vs Expenses Trend",
    "dashboard.topCategories": "Top Expense Categories",
    "dashboard.viewReports": "View Reports",
    "dashboard.totalFinancialPosition": "Total financial position",
    "dashboard.excellentSavingsRate": "Excellent savings rate",
    "dashboard.goodSavingsRate": "Good, aim for 20%",
    "dashboard.needsImprovement": "Needs improvement",
    "dashboard.healthyRatio": "Healthy ratio",
    "dashboard.moderateDebt": "Moderate debt",
    "dashboard.highDebtLevel": "High debt level",
    "dashboard.excellentFinancialShape": "Your finances are in excellent shape! Keep up the great work.",
    "dashboard.goodFinancialHealth": "Your financial health is good. Consider optimizing your savings.",
    "dashboard.fairFinancialHealth": "Your finances need attention. Focus on reducing expenses and increasing savings.",
    "dashboard.poorFinancialHealth": "Your finances need immediate attention. Consider creating a budget plan.",
    "dashboard.assetsVsDebts": "Assets vs Debts",
    "dashboard.financialBalance": "Your financial balance",
    "dashboard.netPosition": "Net Position",
    "dashboard.latestIncomeAndExpenses": "Latest income and expenses",
    "dashboard.ofIncome": "% of income",
    "dashboard.overspending": "Overspending",
    "settings.title": "Settings",
    "settings.subtitle": "Manage your account settings and preferences",
    "settings.profile": "Profile Information",
    "settings.profileDescription": "Update your username and email address",
    "settings.currency": "Currency Preference",
    "settings.currencyDescription": "Choose your preferred currency for displaying amounts",
    "settings.language": "Language Preference",
    "settings.languageDescription": "Choose your preferred language",
    "settings.password": "Change Password",
    "settings.passwordDescription": "Update your password to keep your account secure",
    "settings.username": "Username",
    "settings.email": "Email",
    "settings.newPassword": "New Password",
    "settings.confirmPassword": "Confirm New Password",
    "settings.save": "Save Changes",
    "settings.saving": "Saving...",
    "settings.updatePassword": "Update Password",
    "settings.updating": "Updating...",
    "settings.noChanges": "No changes to save",
    "settings.success": "Profile updated successfully",
    "settings.error": "Failed to update profile",
    "common.loading": "Loading...",
    "common.skipToContent": "Skip to content",
    "common.error": "Error",
    "common.success": "Success",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.view": "View",
    "common.close": "Close",
    "common.update": "Update",
    "common.confirmDelete": "Are you sure you want to delete this?",
    "common.justNow": "Just now",
    "common.minutesAgo": "minute(s) ago",
    "common.hoursAgo": "hour(s) ago",
    "common.daysAgo": "day(s) ago",
    "budget.title": "Budget",
    "budget.subtitle": "Manage your income and expenses",
    "budget.totalIncome": "Total Income",
    "budget.totalExpenses": "Total Expenses",
    "budget.savings": "Savings",
    "budget.income": "Income",
    "budget.expenses": "Expenses",
    "budget.addIncome": "Add Income",
    "budget.addExpense": "Add Expense",
    "budget.sourceName": "Source Name",
    "budget.amount": "Amount",
    "budget.date": "Date",
    "budget.category": "Category",
    "budget.noIncomeRecords": "No income records yet",
    "budget.noExpenseRecords": "No expense records yet",
    "budget.incomeAdded": "Income added successfully",
    "budget.incomeUpdated": "Income updated successfully",
    "budget.expenseAdded": "Expense added successfully",
    "budget.expenseUpdated": "Expense updated successfully",
    "budget.incomeDeleted": "Income deleted successfully",
    "budget.expenseDeleted": "Expense deleted successfully",
    "budget.deleteIncomeConfirm": "Are you sure you want to delete this income?",
    "budget.deleteExpenseConfirm": "Are you sure you want to delete this expense?",
    "budget.update": "Update",
    "budget.showing": "Showing",
    "budget.to": "to",
    "budget.of": "of",
    "budget.entries": "entries",
    "budget.previous": "Previous",
    "budget.next": "Next",
    "netWorth.title": "Net Worth",
    "netWorth.subtitle": "Track your assets and debts",
    "netWorth.totalAssets": "Total Assets",
    "netWorth.totalDebts": "Total Debts",
    "netWorth.assets": "Assets",
    "netWorth.debts": "Debts",
    "netWorth.addAsset": "Add Asset",
    "netWorth.addDebt": "Add Debt",
    "netWorth.interestRate": "Interest Rate (%)",
    "netWorth.dueDate": "Due Date",
    "netWorth.noAssets": "No assets yet",
    "netWorth.noDebts": "No debts yet",
    "netWorth.recordPayment": "Record Payment",
    "netWorth.paymentAmount": "Payment Amount",
    "netWorth.paymentRecorded": "Payment recorded successfully",
    "netWorth.assetDeleted": "Asset deleted successfully",
    "netWorth.debtDeleted": "Debt deleted successfully",
    "netWorth.deleteAssetConfirm": "Are you sure you want to delete this asset?",
    "netWorth.deleteDebtConfirm": "Are you sure you want to delete this debt?",
    "netWorth.optional": "Optional",
    "goals.title": "Goals",
    "goals.subtitle": "Set and track your financial goals",
    "goals.totalGoals": "Total Goals",
    "goals.completed": "Completed",
    "goals.inProgress": "In Progress",
    "goals.budgetGoals": "Budget Goals",
    "goals.debtReduction": "Debt Reduction",
    "goals.netWorthGrowth": "Net Worth Growth",
    "goals.addGoal": "Add Goal",
    "goals.goalName": "Goal Name",
    "goals.targetAmount": "Target Amount",
    "goals.targetDate": "Target Date",
    "goals.noGoals": "No goals yet",
    "goals.goalAdded": "Goal added successfully",
    "goals.goalUpdated": "Goal updated successfully",
    "goals.goalDeleted": "Goal deleted successfully",
    "goals.deleteGoalConfirm": "Are you sure you want to delete this goal?",
    "goals.progress": "Progress",
    "goals.status": "Status",
    "goals.notStarted": "Not Started",
    "goals.completedStatus": "Completed",
    "goals.daysRemaining": "Days Remaining",
    "goals.totalDebt": "Total Debt",
    "goals.paid": "Paid",
    "goals.remaining": "Remaining",
    "goals.overallProgress": "Overall Progress",
    "goals.debtBreakdown": "Debt Breakdown",
    "goals.original": "Original",
    "goals.current": "Current",
    "goals.setTarget": "Set Target",
    "goals.updateTarget": "Update Target",
    "goals.targetNetWorth": "Target Net Worth",
    "goals.currentNetWorth": "Current Net Worth",
    "goals.noNetWorthTarget": "No net worth target set",
    "goals.clickSetTarget": "Click \"Set Target\" to create a net worth growth goal",
    "reports.title": "Reports",
    "reports.subtitle": "View detailed financial reports",
    "reports.monthlyReport": "Monthly Report",
    "reports.netWorthReport": "Net Worth Report",
    "reports.incomeVsExpenses": "Income vs Expenses",
    "reports.categoryBreakdown": "Category Breakdown",
    "reports.generate": "Generate",
    "reports.exportPDF": "Export PDF",
    "reports.exportCSV": "Export CSV",
    "reports.loadingReport": "Loading report...",
    "reports.incomeBySource": "Income by Source",
    "reports.expensesByCategory": "Expenses by Category",
    "reports.totalIncome": "Total Income",
    "reports.totalExpenses": "Total Expenses",
    "reports.savings": "Savings",
    "reports.savingsRate": "Savings Rate",
    "notifications.title": "Notifications",
    "notifications.subtitle": "View your notifications",
    "notifications.totalNotifications": "Total Notifications",
    "notifications.unread": "Unread",
    "notifications.all": "All",
    "notifications.markAllRead": "Mark All Read",
    "notifications.markedAsRead": "Notification marked as read",
    "notifications.deleted": "Notification deleted",
    "notifications.noNotifications": "No notifications yet",
    "notifications.noUnread": "No unread notifications",
    "notifications.viewNotifications": "View your notifications",
    "auth.login": "Login",
    "auth.logout": "Log out",
    "auth.register": "Register",
    "auth.loggingIn": "Logging in...",
    "auth.loginSuccess": "Login successful!",
    "auth.loginFailed": "Login failed",
    "auth.createAccount": "Create Account",
    "auth.creatingAccount": "Creating account...",
    "auth.accountCreated": "Account created successfully!",
    "auth.registrationFailed": "Registration failed",
    "auth.password": "Password",
    "auth.confirmPassword": "Confirm Password",
    "auth.passwordsNotMatch": "Passwords do not match",
    "auth.passwordTooShort": "Password must be at least 6 characters",
    "auth.dontHaveAccount": "Don't have an account?",
    "auth.alreadyHaveAccount": "Already have an account?",
    "auth.welcome": "Welcome",
    "auth.loginToAccess": "Login to access your financial dashboard",
    "auth.registerToStart": "Register to start managing your finances",
    "auth.dataSecure": "Your data is safe and encrypted",
    "auth.freeAccount": "Free Account",
    "auth.freeAccountDescription": "Sign up now and start managing your finances at no cost",
    "auth.dataSecureDescription": "Your data is protected with high-grade encryption",
    "navbar.dashboard": "Dashboard",
    "navbar.budget": "Budget",
    "navbar.netWorth": "Net Worth",
    "navbar.goals": "Goals",
    "navbar.reports": "Reports",
    "navbar.notifications": "Notifications",
    "navbar.settings": "Settings",
    "navbar.more": "More",
    "navbar.logout": "Log out",
  },
  id: {
    "dashboard.title": "Dasbor",
    "dashboard.subtitle": "Ringkasan kesehatan keuangan Anda",
    "dashboard.financialHealth": "Kesehatan Keuangan",
    "dashboard.netWorth": "Kekayaan Bersih",
    "dashboard.savingsRate": "Tingkat Tabungan",
    "dashboard.debtToAssetRatio": "Rasio Utang terhadap Aset",
    "dashboard.monthlyIncome": "Pendapatan Bulanan",
    "dashboard.monthlyExpenses": "Pengeluaran Bulanan",
    "dashboard.savings": "Tabungan",
    "dashboard.thisMonth": "Bulan ini",
    "dashboard.assets": "Aset",
    "dashboard.debts": "Utang",
    "dashboard.recentTransactions": "Transaksi Terbaru",
    "dashboard.viewAll": "Lihat Semua",
    "dashboard.noTransactions": "Tidak ada transaksi terbaru",
    "dashboard.addTransaction": "Tambah Transaksi",
    "dashboard.incomeVsExpenses": "Tren Pendapatan vs Pengeluaran",
    "dashboard.topCategories": "Kategori Pengeluaran Teratas",
    "dashboard.viewReports": "Lihat Laporan",
    "dashboard.totalFinancialPosition": "Total posisi keuangan",
    "dashboard.excellentSavingsRate": "Tingkat tabungan sangat baik",
    "dashboard.goodSavingsRate": "Bagus, targetkan 20%",
    "dashboard.needsImprovement": "Perlu peningkatan",
    "dashboard.healthyRatio": "Rasio sehat",
    "dashboard.moderateDebt": "Utang sedang",
    "dashboard.highDebtLevel": "Tingkat utang tinggi",
    "dashboard.excellentFinancialShape": "Keuangan Anda dalam kondisi sangat baik! Pertahankan kerja bagus Anda.",
    "dashboard.goodFinancialHealth": "Kesehatan keuangan Anda baik. Pertimbangkan untuk mengoptimalkan tabungan Anda.",
    "dashboard.fairFinancialHealth": "Keuangan Anda perlu perhatian. Fokus pada pengurangan pengeluaran dan peningkatan tabungan.",
    "dashboard.poorFinancialHealth": "Keuangan Anda memerlukan perhatian segera. Pertimbangkan untuk membuat rencana anggaran.",
    "dashboard.assetsVsDebts": "Aset vs Utang",
    "dashboard.financialBalance": "Keseimbangan keuangan Anda",
    "dashboard.netPosition": "Posisi Bersih",
    "dashboard.latestIncomeAndExpenses": "Pendapatan dan pengeluaran terbaru",
    "dashboard.ofIncome": "% dari pendapatan",
    "dashboard.overspending": "Pengeluaran berlebihan",
    "settings.title": "Pengaturan",
    "settings.subtitle": "Kelola pengaturan akun dan preferensi Anda",
    "settings.profile": "Informasi Profil",
    "settings.profileDescription": "Perbarui nama pengguna dan alamat email Anda",
    "settings.currency": "Preferensi Mata Uang",
    "settings.currencyDescription": "Pilih mata uang yang Anda inginkan untuk menampilkan jumlah",
    "settings.language": "Preferensi Bahasa",
    "settings.languageDescription": "Pilih bahasa yang Anda inginkan",
    "settings.password": "Ubah Kata Sandi",
    "settings.passwordDescription": "Perbarui kata sandi Anda untuk menjaga keamanan akun",
    "settings.username": "Nama Pengguna",
    "settings.email": "Email",
    "settings.newPassword": "Kata Sandi Baru",
    "settings.confirmPassword": "Konfirmasi Kata Sandi Baru",
    "settings.save": "Simpan Perubahan",
    "settings.saving": "Menyimpan...",
    "settings.updatePassword": "Perbarui Kata Sandi",
    "settings.updating": "Memperbarui...",
    "settings.noChanges": "Tidak ada perubahan untuk disimpan",
    "settings.success": "Profil berhasil diperbarui",
    "settings.error": "Gagal memperbarui profil",
    "common.loading": "Memuat...",
    "common.skipToContent": "Lompat ke konten",
    "common.error": "Kesalahan",
    "common.success": "Berhasil",
    "common.cancel": "Batal",
    "common.save": "Simpan",
    "common.delete": "Hapus",
    "common.edit": "Ubah",
    "common.add": "Tambah",
    "common.view": "Lihat",
    "common.close": "Tutup",
    "common.update": "Perbarui",
    "common.confirmDelete": "Apakah Anda yakin ingin menghapus ini?",
    "common.justNow": "Baru saja",
    "common.minutesAgo": "menit yang lalu",
    "common.hoursAgo": "jam yang lalu",
    "common.daysAgo": "hari yang lalu",
    "budget.title": "Anggaran",
    "budget.subtitle": "Kelola pendapatan dan pengeluaran Anda",
    "budget.totalIncome": "Total Pendapatan",
    "budget.totalExpenses": "Total Pengeluaran",
    "budget.savings": "Tabungan",
    "budget.income": "Pendapatan",
    "budget.expenses": "Pengeluaran",
    "budget.addIncome": "Tambah Pendapatan",
    "budget.addExpense": "Tambah Pengeluaran",
    "budget.sourceName": "Nama Sumber",
    "budget.amount": "Jumlah",
    "budget.date": "Tanggal",
    "budget.category": "Kategori",
    "budget.noIncomeRecords": "Belum ada catatan pendapatan",
    "budget.noExpenseRecords": "Belum ada catatan pengeluaran",
    "budget.incomeAdded": "Pendapatan berhasil ditambahkan",
    "budget.incomeUpdated": "Pendapatan berhasil diperbarui",
    "budget.expenseAdded": "Pengeluaran berhasil ditambahkan",
    "budget.expenseUpdated": "Pengeluaran berhasil diperbarui",
    "budget.incomeDeleted": "Pendapatan berhasil dihapus",
    "budget.expenseDeleted": "Pengeluaran berhasil dihapus",
    "budget.deleteIncomeConfirm": "Apakah Anda yakin ingin menghapus pendapatan ini?",
    "budget.deleteExpenseConfirm": "Apakah Anda yakin ingin menghapus pengeluaran ini?",
    "budget.update": "Perbarui",
    "budget.showing": "Menampilkan",
    "budget.to": "hingga",
    "budget.of": "dari",
    "budget.entries": "entri",
    "budget.previous": "Sebelumnya",
    "budget.next": "Selanjutnya",
    "netWorth.title": "Kekayaan Bersih",
    "netWorth.subtitle": "Lacak aset dan utang Anda",
    "netWorth.totalAssets": "Total Aset",
    "netWorth.totalDebts": "Total Utang",
    "netWorth.assets": "Aset",
    "netWorth.debts": "Utang",
    "netWorth.addAsset": "Tambah Aset",
    "netWorth.addDebt": "Tambah Utang",
    "netWorth.interestRate": "Tingkat Bunga (%)",
    "netWorth.dueDate": "Tanggal Jatuh Tempo",
    "netWorth.noAssets": "Belum ada aset",
    "netWorth.noDebts": "Belum ada utang",
    "netWorth.recordPayment": "Catat Pembayaran",
    "netWorth.paymentAmount": "Jumlah Pembayaran",
    "netWorth.paymentRecorded": "Pembayaran berhasil dicatat",
    "netWorth.assetDeleted": "Aset berhasil dihapus",
    "netWorth.debtDeleted": "Utang berhasil dihapus",
    "netWorth.deleteAssetConfirm": "Apakah Anda yakin ingin menghapus aset ini?",
    "netWorth.deleteDebtConfirm": "Apakah Anda yakin ingin menghapus utang ini?",
    "netWorth.optional": "Opsional",
    "goals.title": "Tujuan",
    "goals.subtitle": "Tetapkan dan lacak tujuan keuangan Anda",
    "goals.totalGoals": "Total Tujuan",
    "goals.completed": "Selesai",
    "goals.inProgress": "Sedang Berlangsung",
    "goals.budgetGoals": "Tujuan Anggaran",
    "goals.debtReduction": "Pengurangan Utang",
    "goals.netWorthGrowth": "Pertumbuhan Kekayaan Bersih",
    "goals.addGoal": "Tambah Tujuan",
    "goals.goalName": "Nama Tujuan",
    "goals.targetAmount": "Jumlah Target",
    "goals.targetDate": "Tanggal Target",
    "goals.noGoals": "Belum ada tujuan",
    "goals.goalAdded": "Tujuan berhasil ditambahkan",
    "goals.goalUpdated": "Tujuan berhasil diperbarui",
    "goals.goalDeleted": "Tujuan berhasil dihapus",
    "goals.deleteGoalConfirm": "Apakah Anda yakin ingin menghapus tujuan ini?",
    "goals.progress": "Kemajuan",
    "goals.status": "Status",
    "goals.notStarted": "Belum Dimulai",
    "goals.completedStatus": "Selesai",
    "goals.daysRemaining": "Hari Tersisa",
    "goals.totalDebt": "Total Utang",
    "goals.paid": "Dibayar",
    "goals.remaining": "Tersisa",
    "goals.overallProgress": "Kemajuan Keseluruhan",
    "goals.debtBreakdown": "Rincian Utang",
    "goals.original": "Awal",
    "goals.current": "Saat Ini",
    "goals.setTarget": "Tetapkan Target",
    "goals.updateTarget": "Perbarui Target",
    "goals.targetNetWorth": "Target Kekayaan Bersih",
    "goals.currentNetWorth": "Kekayaan Bersih Saat Ini",
    "goals.noNetWorthTarget": "Belum ada target kekayaan bersih",
    "goals.clickSetTarget": "Klik \"Tetapkan Target\" untuk membuat tujuan pertumbuhan kekayaan bersih",
    "reports.title": "Laporan",
    "reports.subtitle": "Lihat laporan keuangan detail",
    "reports.monthlyReport": "Laporan Bulanan",
    "reports.netWorthReport": "Laporan Kekayaan Bersih",
    "reports.incomeVsExpenses": "Pendapatan vs Pengeluaran",
    "reports.categoryBreakdown": "Rincian Kategori",
    "reports.generate": "Buat",
    "reports.exportPDF": "Ekspor PDF",
    "reports.exportCSV": "Ekspor CSV",
    "reports.loadingReport": "Memuat laporan...",
    "reports.incomeBySource": "Pendapatan per Sumber",
    "reports.expensesByCategory": "Pengeluaran per Kategori",
    "reports.totalIncome": "Total Pendapatan",
    "reports.totalExpenses": "Total Pengeluaran",
    "reports.savings": "Tabungan",
    "reports.savingsRate": "Tingkat Tabungan",
    "notifications.title": "Notifikasi",
    "notifications.subtitle": "Lihat notifikasi Anda",
    "notifications.totalNotifications": "Total Notifikasi",
    "notifications.unread": "Belum Dibaca",
    "notifications.all": "Semua",
    "notifications.markAllRead": "Tandai Semua Dibaca",
    "notifications.markedAsRead": "Notifikasi ditandai sebagai dibaca",
    "notifications.deleted": "Notifikasi dihapus",
    "notifications.noNotifications": "Belum ada notifikasi",
    "notifications.noUnread": "Tidak ada notifikasi yang belum dibaca",
    "notifications.viewNotifications": "Lihat notifikasi Anda",
    "auth.login": "Masuk",
    "auth.logout": "Keluar",
    "auth.register": "Daftar",
    "auth.loggingIn": "Masuk...",
    "auth.loginSuccess": "Berhasil masuk!",
    "auth.loginFailed": "Gagal masuk",
    "auth.createAccount": "Buat Akun",
    "auth.creatingAccount": "Membuat akun...",
    "auth.accountCreated": "Akun berhasil dibuat!",
    "auth.registrationFailed": "Pendaftaran gagal",
    "auth.password": "Kata Sandi",
    "auth.confirmPassword": "Konfirmasi Kata Sandi",
    "auth.passwordsNotMatch": "Kata sandi tidak cocok",
    "auth.passwordTooShort": "Kata sandi minimal 6 karakter",
    "auth.dontHaveAccount": "Tidak punya akun?",
    "auth.alreadyHaveAccount": "Sudah punya akun?",
    "auth.welcome": "Selamat Datang",
    "auth.loginToAccess": "Masuk untuk mengakses dashboard keuangan Anda",
    "auth.registerToStart": "Daftar untuk mulai mengelola keuangan Anda",
    "auth.dataSecure": "Data Anda aman dan terenkripsi",
    "auth.freeAccount": "Akun Gratis",
    "auth.freeAccountDescription": "Daftar sekarang dan mulai kelola keuangan Anda tanpa biaya",
    "auth.dataSecureDescription": "Data Anda dilindungi dengan enkripsi tingkat tinggi",
    "navbar.dashboard": "Dasbor",
    "navbar.budget": "Anggaran",
    "navbar.netWorth": "Kekayaan Bersih",
    "navbar.goals": "Tujuan",
    "navbar.reports": "Laporan",
    "navbar.notifications": "Notifikasi",
    "navbar.settings": "Pengaturan",
    "navbar.more": "Lainnya",
    "navbar.logout": "Keluar",
  },
};

/**
 * Get translation for a key
 */
export function t(key: TranslationKey, language: Language = "en"): string {
  return translations[language]?.[key] || translations.en[key] || key;
}

/**
 * Validate language code
 */
export function isValidLanguage(code: string): code is Language {
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === code);
}

